import React from 'react';
import { ChevronDown, ChevronRight, Building2, Home, MapPin, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { HighlightState } from './RecallAssignmentCard';

interface DealerGroupHeaderProps {
  dealerName: string;
  dealerAddress?: string;
  dealerCity?: string;
  dealerRegion?: string;
  dealerPostalCode?: string;
  recallCount: number;
  selectedCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelectAll: (selected: boolean) => void;
  allSelected: boolean;
  someSelected: boolean;
  className?: string;
  onClearDealer?: () => void;
  hasAssignedRecalls?: boolean;
  groupHighlightState?: HighlightState;
  /** Vehix/recall stops: primary; manual stops: accent; default: muted */
  dealerBrand?: 'vehix' | 'manual' | 'default';
  /** Customer manual stop (a household): house icon instead of a building */
  isCustomer?: boolean;
}

/**
 * Collapsible header for grouping recalls by dealer
 * Shows dealer name, location, and recall count with select-all checkbox
 */
// eslint-disable-next-line complexity
export const DealerGroupHeader: React.FC<DealerGroupHeaderProps> = ({
  dealerName,
  dealerAddress,
  dealerCity,
  dealerRegion,
  dealerPostalCode,
  recallCount,
  selectedCount,
  isExpanded,
  onToggleExpand,
  onSelectAll,
  allSelected,
  someSelected,
  className = '',
  onClearDealer,
  hasAssignedRecalls = false,
  groupHighlightState = 'none',
  dealerBrand = 'default',
  isCustomer = false
}) => {
  const LocationIcon = isCustomer ? Home : Building2;
  const handleCheckboxChange = (checked: boolean) => {
    onSelectAll(checked);
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    // Don't toggle expand if clicking checkbox area
    const target = e.target as HTMLElement;
    if (target.closest('.dealer-checkbox') || target.closest('[role="checkbox"]')) {
      return;
    }
    onToggleExpand();
  };

  // Format full address string (like route preview page)
  const addressParts = [];
  if (dealerAddress && dealerAddress.trim() && dealerAddress !== 'Address not available') {
    addressParts.push(dealerAddress.trim());
  }
  if (dealerCity && dealerCity.trim()) {
    addressParts.push(dealerCity.trim());
  }
  if (dealerRegion && dealerRegion.trim()) {
    // Append postal code to state (e.g., "TX 75201")
    const stateWithZip = dealerPostalCode?.trim()
      ? `${dealerRegion.trim()} ${dealerPostalCode.trim()}`
      : dealerRegion.trim();
    addressParts.push(stateWithZip);
  } else if (dealerPostalCode?.trim()) {
    // If no region but has postal code, add it standalone
    addressParts.push(dealerPostalCode.trim());
  }
  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={handleHeaderClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleExpand();
        }
      }}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200',
        'bg-muted/50 hover:bg-muted/70 dark:bg-muted/30 dark:hover:bg-muted/50',
        groupHighlightState === 'blue' &&
          'ring-2 ring-blue-400 ring-offset-1 bg-blue-50/50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-700',
        groupHighlightState === 'red' &&
          'ring-2 ring-red-400 ring-offset-1 bg-red-50/50 dark:bg-red-950/30 border-red-300 dark:border-red-700',
        groupHighlightState === 'none' && 'border border-transparent hover:border-border/50',
        className
      )}
    >
      {/* Expand/Collapse Icon */}
      <div className='text-muted-foreground'>
        {isExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
      </div>

      {/* Select All Checkbox */}
      <div
        role='presentation'
        className='dealer-checkbox'
        onClick={e => {
          e.stopPropagation();
        }}
        onKeyDown={e => {
          e.stopPropagation();
        }}
      >
        <Checkbox
          checked={!!allSelected}
          onCheckedChange={checked => {
            handleCheckboxChange(checked === true);
          }}
          className={cn(
            'transition-all cursor-pointer',
            someSelected && !allSelected && 'data-[state=checked]:bg-primary/50'
          )}
        />
      </div>

      {/* Dealer icon — Vehix (recall) groups use primary; manual stops use accent */}
      {dealerBrand === 'vehix' ? (
        <div className='p-1.5 rounded-full bg-primary/15 text-primary flex-shrink-0'>
          <LocationIcon className='h-4 w-4' aria-hidden />
        </div>
      ) : dealerBrand === 'manual' ? (
        <div className='p-1.5 rounded-full bg-accent/15 text-accent flex-shrink-0'>
          <LocationIcon className='h-4 w-4' aria-hidden />
        </div>
      ) : (
        <LocationIcon className='h-4 w-4 text-muted-foreground flex-shrink-0' aria-hidden />
      )}

      {/* Dealer Info */}
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2'>
          <span className='font-semibold text-sm text-foreground truncate'>{dealerName}</span>
        </div>
        {fullAddress && (
          <div className='flex items-center gap-1 text-xs text-muted-foreground'>
            <MapPin className='h-3 w-3' />
            <span>{fullAddress}</span>
          </div>
        )}
      </div>

      {/* Recall Count Badge and Clear Button */}
      <div className='flex items-center gap-2'>
        {selectedCount > 0 && (
          <Badge
            variant='default'
            className={cn(
              'text-xs',
              dealerBrand === 'manual'
                ? 'bg-accent/90 hover:bg-accent'
                : 'bg-primary/90 hover:bg-primary'
            )}
          >
            {selectedCount} selected
          </Badge>
        )}
        {/* Count badge: primary for recalls, accent for manual stops */}
        <Badge
          variant={dealerBrand === 'vehix' || dealerBrand === 'manual' ? 'outline' : 'secondary'}
          className={cn(
            'text-xs font-semibold',
            dealerBrand === 'vehix' &&
              'border-primary/40 bg-primary/15 text-primary dark:bg-primary/20',
            dealerBrand === 'manual' &&
              'border-accent/40 bg-accent/15 text-accent dark:bg-accent/20'
          )}
        >
          {recallCount} {dealerBrand === 'manual' ? 'Job' : 'Recall'}
          {recallCount !== 1 ? 's' : ''}
        </Badge>
        {onClearDealer && hasAssignedRecalls && (
          <Button
            size='sm'
            variant='ghost'
            onClick={e => {
              e.stopPropagation();
              onClearDealer();
            }}
            className='h-7 text-xs p-2'
            title='Clear all recalls for this dealer'
          >
            <Trash2 className='h-3 w-3' />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DealerGroupHeader;
